import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import {
  Dimmer,
  Icon,
  Table,
  Grid,
  Button,
  Segment,
  Modal,
  Header,
  Loader,
  Input,
} from 'semantic-ui-react';
import PAYMENT_TYPES from '../constants/paymentTypes';
import CurrencySymbol from '../components/CurrencySymbol';
import LoanAmount from '../components/LoanAmount';
import ContentExpand from '../components/ContentExpand';
import TextExpand from '../components/TextExpand';
import UploadPictureForm from '../containers/BookingPaymentPictures/Form';
import DeleteConfirm from '../containers/modals/DeleteConfirm';
import UploadModal from '../components/UploadModal';
import Timestamp from '../components/Timestamp';
import ViewLarge from '../containers/modals/ViewLarge';
import fileSize from '../utils/fileSize';
import valueToText from '../utils/valueToText';
import { centsToFloat } from '../utils/numbers/centsFloat';
import { flashSuccessMessage, flashErrorMessage } from '../actions/flash';
import { fetchM } from '../actions/fetchM';
import { TRANSLATION_FILES } from '../constants/translation';


class BookingFeePaymentList extends Component {
  static propTypes = {
    auth: PropTypes.shape({
      groups: PropTypes.array,
    }),
    bookingID: PropTypes.string.isRequired,
    data: PropTypes.oneOfType([
      PropTypes.array,
    ]),
    fetchNewBooking: PropTypes.func,
    token: PropTypes.string.isRequired,
    listOnly: PropTypes.bool,
    isSubModal: PropTypes.bool,
    projectCurrencies: PropTypes.arrayOf(PropTypes.object),
    t: PropTypes.func,
  }
  static defaultProps = {
    data: [],
  }
  static contextTypes = {
    store: PropTypes.object,
  }
  constructor(props) {
    super(props);
    this.state = {
      cancelModalOpen: false,
      cancelModalID: '',
      cancelModalAmount: '',
      loading: false,
      pictureListModalOpen: false,
      pictureListModalID: '',
      showLarge: false,
      selectedPicture: null,
      slideshow: false,
      showDeletePictureConfirm: false,
      deleteFile: null,
    };
  }
  setComponentState(state = {}) {
    return new Promise((res) => {
      this.setState(state, () => {
        res(state);
      });
    });
  }
  setLoading(loading = false) {
    return new Promise((res) => {
      this.setState({ loading }, () => {
        res(loading);
      });
    });
  }
  getPicturesLength = (data) => {
    if (data && data.paymentPictures && data.paymentPictures.length > 0) {
      return data.paymentPictures.length;
    }
    return 0;
  }
  getPaymentPictures = () => {
    const { pictureListModalID } = this.state;
    const { data } = this.props;
    const payment = data.find(d => d.id === pictureListModalID);
    if (payment && payment.paymentPictures && payment.paymentPictures.length > 0) {
      return payment.paymentPictures;
    }
    return [];
  }
  getPaymentStatus(payment, textWidth = 'auto') {
    const { t } = this.props;
    if (payment.paymentMethodTypeID === 'online_payment' && payment.onlinePayment && payment.onlinePayment.requestStatus) {
      let onlinePaymentURL = '';
      if (payment.onlinePayment.requestStatus === 'payment_submitted' || payment.onlinePayment.requestStatus === 'payment_completed') {
        onlinePaymentURL = payment.onlinePayment.responsePaymentURL;
      }
      if (onlinePaymentURL !== '') {
        return (
          <div>
            <a href={payment.onlinePayment.responsePaymentURL} rel="noopener noreferrer" target="_blank">{t(valueToText(this.ONLINE_PAYMENT_STATUS, payment.onlinePayment.requestStatus), { ns: TRANSLATION_FILES.constants })}</a><br />
          </div>
        );
      }
      return (
        <div>
          {t(valueToText(this.ONLINE_PAYMENT_STATUS, payment.onlinePayment.requestStatus), { ns: TRANSLATION_FILES.constants })}<br />
        </div>
      );
    }
    return (
      <div>
        {t(valueToText(this.PAYMENT_STATUS, payment.status), { ns: TRANSLATION_FILES.constants })}<br />
        <div className={textWidth !== 'auto' ? 'text_expand_single' : ''} style={{ width: textWidth, fontSize: '80%' }}>{payment.cancelledReason}</div>
      </div>
    );
  }
  checkDisabled(direction = 'right') {
    const { selectedPicture } = this.state;
    if (this.getPaymentPictures() && this.getPaymentPictures().length > 0 && selectedPicture) {
      const index = this.getPaymentPictures().indexOf(selectedPicture);
      if (direction === 'left') {
        return index - 1 >= 0;
      }
      return index + 1 < this.getPaymentPictures().length;
    }
    return false;
  }
  isOfflinePayment = (payment) => {
    if (payment.paymentMethodTypeID === 'online_payment' || payment.status === 'deduction') {
      return false;
    }
    return true;
  }
  isCancelled = (payment) => {
    if (payment.cancelledReason !== '' && (payment.status === 'cancelled' || payment.status === 'deduction')) {
      return true;
    }
    return false;
  }
  async cancelPayment() {
    const { store } = this.context;
    await this.setLoading(true);
    const data = {
      cancelledReason: this.state.cancelModalReason,
    };
    try {
      await store.dispatch(fetchM(`/bookings/${this.props.bookingID}/booking_fee_payments/${this.state.cancelModalID}/`, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': this.props.token,
        },
        body: JSON.stringify(data),
      }));
    } catch (err) {
      await this.setLoading(false);
      Bugsnag.notifyException(err);
      if (err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      store.dispatch(flashErrorMessage(err));
    }
    await this.setLoading(false);
    await this.setComponentState({
      cancelModalID: '',
      cancelModalAmount: '',
      cancelModalReason: '',
    });
    if (this.props.fetchNewBooking) {
      this.props.fetchNewBooking();
    }
  }
  async uploadPictures(e, body) {
    e.preventDefault();
    await this.setLoading(true);
    const { t } = this.props;
    const { store } = this.context;
    store.dispatch(fetchM(`/bookings/${this.props.bookingID}/pictures/?FPID=${this.state.pictureListModalID}`, {
      credentials: 'include',
      method: 'POST',
      headers: {
        // Let browser decide Content-Type
        'X-Csrf-Token': this.props.token,
      },
      body,
    })).then((res) => {
      res.text().then(() => {
        if (this.props.fetchNewBooking) {
          this.props.fetchNewBooking(() => {
            this.setLoading(false);
          });
        }
      });
      if (!this.props.fetchNewBooking) this.setLoading(false);
      this.uploadModal.close();
      store.dispatch(flashSuccessMessage({ header: t('Upload Success', { ns: TRANSLATION_FILES.translation }), message: t('Picture(s) successfully uploaded', { ns: TRANSLATION_FILES.translation }) }));
    }).catch((err) => {
      this.setLoading(false);
      if (err.res && err.res.status >= 400 && err.res.status < 500) {
        err.res.text().then((message) => {
          store.dispatch(flashErrorMessage({ message }));
        });
        return;
      }
      store.dispatch(flashErrorMessage(err));
      this.handleCancel();
    });
  }
  async deletePicture(id) {
    const { store } = this.context;
    const { t } = this.props;
    await this.setLoading(true);
    try {
      await store.dispatch(fetchM(`/bookings/${this.props.bookingID}/pictures/${id}/`, {
        credentials: 'include',
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-Csrf-Token': this.props.token,
        },
      }));
    } catch (err) {
      Bugsnag.notifyException(err);
      if (err.res.status >= 400 && err.res.status < 500) {
        const message = await err.res.text();
        store.dispatch(flashErrorMessage({ message }));
        return;
      }
      store.dispatch(flashErrorMessage(err));
    }
    if (this.props.fetchNewBooking) await this.props.fetchNewBooking();
    await this.setLoading(false);
    store.dispatch(flashSuccessMessage({ header: t('Delete Success', { ns: TRANSLATION_FILES.translation }), message: t('Picture successfully deleted', { ns: TRANSLATION_FILES.translation }) }));
  }
  handleCancelPayment = (e, payment) => {
    e.preventDefault();
    this.setState({
      cancelModalOpen: true,
      cancelModalID: payment.id,
      cancelModalAmount: payment.amountCents,
    });
  }
  handleChangeCancelReason = (e, data) => {
    this.setState({
      cancelModalReason: data.value,
    });
  }
  handleCloseCancelModal = (e) => {
    e.preventDefault();
    this.setState({
      loading: false,
      cancelModalOpen: false,
      cancelModalID: '',
      cancelModalAmount: '',
      cancelModalReason: '',
    });
  }
  handleCancelPaymentDelete = (e) => {
    e.preventDefault();
    this.setState({
      cancelModalOpen: false,
    }, () => {
      this.cancelPayment();
    });
  }
  handleCheckBeforeClose = () => {
    if (this.form.state.uploads.length > 0) {
      this.uploadModal.close();
      return true;
    }
    return false;
  }
  handleViewPictures = (e, payment) => {
    e.preventDefault();
    this.setState({
      pictureListModalOpen: true,
      pictureListModalID: payment.id,
    });
  }
  handleClosePictureListModal = (e) => {
    e.preventDefault();
    this.setState({
      loading: false,
      pictureListModalOpen: false,
      pictureListModalID: '',
    }, () => {
      if (this.props.isSubModal) {
        document.body.className += ' dimmable dimmed scrolling';
      }
    });
  }
  handleCloseLarge = (e) => {
    e.preventDefault();
    this.setState({
      showLarge: false,
      selectedPicture: null,
      slideshow: false,
    });
  }
  handleViewLargeSlide = (e, data) => {
    e.preventDefault();
    this.setState({
      selectedPicture: data,
      showLarge: true,
      slideshow: true,
    });
  }
  handleSlidePicture = (e, direction = 'right') => {
    const { selectedPicture } = this.state;
    const index = this.getPaymentPictures().indexOf(selectedPicture);
    const nextIndex = direction === 'left' ? index - 1 : index + 1;
    this.setState({
      selectedPicture: this.getPaymentPictures()[nextIndex],
    });
  }
  handleDeletePicture = (e, file) => {
    e.preventDefault();
    this.setState({
      showDeletePictureConfirm: true,
      deleteFile: file,
    });
  }
  handleCancelDeletePicture = (e) => {
    e.preventDefault();
    this.setState({
      showDeletePictureConfirm: false,
      deleteFile: null,
    });
  }
  handleConfirmDeletePicture = (e) => {
    e.preventDefault();
    const fileID = this.state.deleteFile.id;
    this.setState({
      showDeletePictureConfirm: false,
      deleteFile: null,
    }, () => {
      this.deletePicture(fileID);
    });
  }
  PAYMENT_STATUS = [
    { text: 'Payment Made', value: 'paid' },
    { text: 'Pending', value: 'pending' },
    { text: 'Cancelled', value: 'cancelled' },
    { text: 'Deduction', value: 'deduction' },
  ];
  ONLINE_PAYMENT_STATUS = [
    { text: 'Pending Registration', value: 'new' },
    { text: 'Approved', value: 'approved' },
    { text: 'Rejected', value: 'rejected' },
    { text: 'Payment Requested', value: 'payment_submitted' },
    { text: 'Payment Failed', value: 'payment_failed' },
    { text: 'Paid', value: 'payment_completed' },
  ];
  render() {
    const { t, data, projectCurrencies = [] } = this.props;
    const {
      cancelModalOpen,
      cancelModalAmount,
      cancelModalReason,
      pictureListModalOpen,
      deleteFile,
    } = this.state;
    return (
      <Grid>
        <Dimmer style={{ zIndex: 20 }} active={this.state.loading} inverted>
          <Loader inline="centered">{t('Loading', { ns: TRANSLATION_FILES.translation })}</Loader>
        </Dimmer>
        <DeleteConfirm
          open={this.state.showDeletePictureConfirm}
          onClose={this.handleCancelDeletePicture}
          onConfirm={this.handleConfirmDeletePicture}
          name={deleteFile ? deleteFile.name : ''}
        />
        <ViewLarge
          open={this.state.showLarge}
          onClose={this.handleCloseLarge}
          picture={this.state.selectedPicture}
          onSlide={this.handleSlidePicture}
          disabledLeft={!this.checkDisabled('left')}
          disabledRight={!this.checkDisabled('right')}
          slideshow={this.state.slideshow}
        />
        <Modal
          size="small"
          open={cancelModalOpen}
          onClose={this.handleCloseCancelModal}
        >
          <Header icon="x" content={t('Cancel Payment', { ns: TRANSLATION_FILES.screens })} />
          <Modal.Content>
            <Modal.Description>
              {t('Cancel and deduct payment of', { ns: TRANSLATION_FILES.translation })}&nbsp;
              <b>
                <LoanAmount
                  projectCurrencies={projectCurrencies}
                  format="0,000,000.00"
                  precise
                  amount={centsToFloat(cancelModalAmount)}
                  prefix={<CurrencySymbol />}
                />
              </b>
              ?
              <br /><br />
              <Input
                fluid
                onChange={this.handleChangeCancelReason}
                type="text"
                placeholder="Cancel reason"
                value={cancelModalReason}
              />
            </Modal.Description>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={this.handleCloseCancelModal} icon="cancel" content="No" />
            <Button positive onClick={this.handleCancelPaymentDelete} icon="checkmark" content="Yes" />
          </Modal.Actions>
        </Modal>
        <Modal
          open={pictureListModalOpen}
          onClose={this.handleClosePictureListModal}
        >
          <Dimmer style={{ zIndex: 20 }} active={this.state.loading} inverted>
            <Loader inline="centered">Loading</Loader>
          </Dimmer>
          <Modal.Header>
            <Grid>
              <Grid.Row verticalAlign="middle">
                <Grid.Column mobile={8} tablet={8} computer={8}>
                  <h3><Icon name="picture" size="large" /> {t('Picture Attachments', { ns: TRANSLATION_FILES.translation })}</h3>
                </Grid.Column>
                <Grid.Column mobile={8} tablet={8} computer={8} textAlign="right">
                  {
                    !this.props.listOnly ?
                      <UploadModal
                        ref={(ref) => { this.uploadModal = ref; }}
                        header="pictures"
                        loading={this.state.loading}
                        onConfirm={(e, file) => {
                          this.uploadPictures(e, file);
                        }}
                        onClose={this.handleCheckBeforeClose}
                        content={opt => (
                          <UploadPictureForm
                            ref={(ref) => { this.form = ref; }}
                            loading={this.state.loading}
                            onSubmit={opt.onConfirm}
                          />
                        )}
                      />
                    : ''
                  }
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Modal.Header>
          <Modal.Content scrolling>
            <Modal.Description>
              <Segment.Group>
                { this.getPaymentPictures().length > 0 ?
                    this.getPaymentPictures().map((file, id) => (
                      <Segment key={id}>
                        <Grid
                          columns="equal"
                          verticalAlign="middle"
                          as="a"
                        >
                          <Grid.Column mobile={16} tablet={10} computer={11} textAlign="left" onClick={e => this.handleViewLargeSlide(e, file)}>
                            <Icon name="file image outline" />
                            {file.name}
                            <span className="mobile-hide"> ({fileSize(file.size)})</span>
                            <small className="large-hide small-hide tablet-hide"><br />{fileSize(file.size)}</small>
                          </Grid.Column>
                          <Grid.Column mobile={16} tablet={6} computer={5} textAlign="right">
                            <small onClick={e => this.handleViewLargeSlide(e, file)} style={{ paddingRight: '1em' }}>
                              <Timestamp timestamp={file.createdAt} format="DD/MM/YY hh:mm a" />
                            </small>
                            <Button
                              compact
                              negative
                              type="button"
                              size="tiny"
                              icon="trash"
                              onClick={e => this.handleDeletePicture(e, file)}
                            />
                          </Grid.Column>
                        </Grid>
                      </Segment>
                    ))
                  : <Segment textAlign="center">{t('No pictures found', { ns: TRANSLATION_FILES.screens })}</Segment>
                }
              </Segment.Group>
            </Modal.Description>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={this.handleClosePictureListModal} content="Close" />
          </Modal.Actions>
        </Modal>
        <Grid.Row>
          <Grid.Column mobile={16} tablet={16} computer={16} style={{ overflowX: 'auto' }}>
            {
              data && data.length > 0 ?
                <Table
                  unstackable
                >
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>{this.props.t('Date', { ns: TRANSLATION_FILES.translation })}</Table.HeaderCell>
                      <Table.HeaderCell>{this.props.t('Amount', { ns: TRANSLATION_FILES.translation })}</Table.HeaderCell>
                      <Table.HeaderCell>{this.props.t('Type', { ns: TRANSLATION_FILES.translation })}</Table.HeaderCell>
                      <Table.HeaderCell>{this.props.t('Reference', { ns: TRANSLATION_FILES.translation })}</Table.HeaderCell>
                      <Table.HeaderCell>{this.props.t('Attachments', { ns: TRANSLATION_FILES.translation })}</Table.HeaderCell>
                      <Table.HeaderCell>{this.props.t('Status', { ns: TRANSLATION_FILES.translation })}</Table.HeaderCell>
                      {
                        !this.props.listOnly &&
                        <Table.HeaderCell />
                      }
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {
                      data.map((payment, idx) => (
                        <Table.Row key={idx}>
                          <Table.Cell width={3}>
                            <Timestamp timestamp={payment.createdAt} format="DD/MM/YY hh:mm a" />
                          </Table.Cell>
                          <Table.Cell width={2}>
                            <LoanAmount
                              projectCurrencies={projectCurrencies}
                              format="0,000,000.00"
                              amount={centsToFloat(payment.amountCents)}
                              prefix={<CurrencySymbol />}
                            />
                          </Table.Cell>
                          <Table.Cell width={2}>
                            {t(valueToText(PAYMENT_TYPES, payment.paymentMethodTypeID), { ns: TRANSLATION_FILES.constants })}
                          </Table.Cell>
                          <Table.Cell width={4}>
                            <TextExpand
                              width="150px"
                              text={payment.reference}
                            />
                          </Table.Cell>
                          <Table.Cell width={2}>
                            {
                              this.isOfflinePayment(payment) ?
                                <button
                                  className="a-button-blue"
                                  onClick={e => this.handleViewPictures(e, payment)}
                                  title="View"
                                >
                                  {
                                    this.getPicturesLength(payment) > 0 ?
                                    `${this.getPicturesLength(payment)} ${t('Attachment', { ns: TRANSLATION_FILES.screens })}`
                                    : t('None', { ns: TRANSLATION_FILES.translation })
                                  }
                                </button>
                              : 'n/a'
                            }
                          </Table.Cell>
                          <Table.Cell width={2}>
                            <ContentExpand
                              width="150px"
                              shortText={this.getPaymentStatus(payment, '150px')}
                              fullContent={
                                this.isCancelled(payment) ?
                                  <div>
                                    {this.getPaymentStatus(payment)}
                                  </div>
                                : ''
                              }
                              expandable={this.isCancelled(payment)}
                            />
                          </Table.Cell>
                          {
                            !this.props.listOnly &&
                            <Table.Cell width={1}>
                              {(payment.status === 'paid' && payment.paymentMethodTypeID !== 'online_payment') &&
                                <Button onClick={e => this.handleCancelPayment(e, payment)} size="mini" floated="right" negative icon><Icon name="x" /></Button>
                              }
                            </Table.Cell>
                          }
                        </Table.Row>
                      ))
                    }
                  </Table.Body>
                </Table>
              :
                <Segment textAlign="center">
                  {t('No payments found', { ns: TRANSLATION_FILES.screens })}
                </Segment>
            }
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  }
}

export default withTranslation([TRANSLATION_FILES.screens, TRANSLATION_FILES.translation])(BookingFeePaymentList);



// WEBPACK FOOTER //
// ./src/components/BookingFeePaymentList.jsx